import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { captureApiError, type ExtraTags } from './sentryCapture';

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    _sentryReportScope?: NetworkReportScope;
    _sentryAuthReplay?: boolean;
  }
}

// The class keeps identity when Axios merges replay configs; plain objects are cloned.
export class NetworkReportScope {
  #failures = new Set<AxiosError>();
  #discarded = false;
  #finalTags: ExtraTags | undefined;

  get discarded(): boolean {
    return this.#discarded;
  }

  record(error: AxiosError): void {
    if (this.#discarded || this.#failures.has(error)) return;
    this.#failures.add(error);
    if (this.#finalTags) captureApiError(error, this.#finalTags);
  }

  discard(): void {
    this.#discarded = true;
    this.#failures.clear();
  }

  reportFailures(tags: ExtraTags): void {
    if (this.#discarded || this.#finalTags) return;
    this.#finalTags = tags;
    for (const failure of this.#failures) captureApiError(failure, tags);
  }
}

let activeScope: NetworkReportScope | undefined;
const ownerScopes = new WeakMap<object, NetworkReportScope>();
const mutationOwners = new WeakMap<object, object>();

export function bindMutationReportOwner(context: object, mutation: object): void {
  mutationOwners.set(context, mutation);
}

export function mutationReportOwner(context: object): object | undefined {
  return mutationOwners.get(context);
}

export function bindNetworkReportScope(config: InternalAxiosRequestConfig): void {
  config._sentryReportScope =
    activeScope ?? (config._sentryAuthReplay ? config._sentryReportScope : undefined);
  delete config._sentryAuthReplay;
}

export function networkReportScope(owner: object): NetworkReportScope | undefined {
  return ownerScopes.get(owner);
}

export function runWithNetworkReportScope<T>(
  owner: object | undefined,
  operation: () => T | Promise<T>
): Promise<T> {
  if (!owner) return Promise.resolve(operation());
  const scope = new NetworkReportScope();
  ownerScopes.set(owner, scope);
  const previous = activeScope;
  activeScope = scope;
  let result: T | Promise<T>;
  try {
    result = operation();
  } finally {
    // Never leave ambient ownership active across await: direct calls can run meanwhile.
    activeScope = previous;
  }
  return Promise.resolve(result).then((value) => {
    scope.reportFailures({});
    return value;
  });
}
